/**
 * SuperAdmin Reassign — pending orders + faculty picker + reassign action.
 *
 * Feb 2026: adds a "stale nudge" that surfaces requests waiting >24h so the
 * SuperAdmin can hand them to another faculty before a student escalates.
 *
 * Uses (unchanged endpoints):
 *   GET /superadmin/pending-approvals   (all pending orders — includes created_at)
 *   GET /admins/faculty-list            (target faculty list)
 *   PUT /superadmin/orders/{id}/reassign-faculty { new_admin_id }
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UserCog, ArrowRight, AlertOctagon, Clock } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { shortRef } from '../../admin/ui';

const STALE_MS = 24 * 60 * 60 * 1000;

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const waitingLabel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const ms = Date.now() - d.getTime();
  if (ms < 60 * 60 * 1000) return `${Math.max(1, Math.round(ms / 60000))}m`;
  if (ms < 24 * 60 * 60 * 1000) return `${Math.round(ms / (60 * 60 * 1000))}h`;
  return `${Math.round(ms / (24 * 60 * 60 * 1000))}d`;
};

const isStale = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && (Date.now() - d.getTime()) > STALE_MS;
};

export default function ReassignPage({ ctx }) {
  const { showToast } = ctx;
  const [pending, setPending] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState({}); // { orderId: newAdminId }
  const [saving, setSaving] = useState(null);
  const [staleOnly, setStaleOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, f] = await Promise.all([
        apiClient.get(endpoints.orders.superadminPending),
        apiClient.get(endpoints.orders.facultyList),
      ]);
      setPending(Array.isArray(p.data) ? p.data : []);
      setFaculty(f.data?.faculty || []);
    } catch (e) {
      showToast && showToast('Failed to load pending queue', 'err');
    } finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  // Re-render every minute so the "waiting" column stays accurate without
  // a full endpoint round-trip.
  useEffect(() => {
    const t = window.setInterval(() => setPending((p) => p.slice()), 60000);
    return () => window.clearInterval(t);
  }, []);

  const staleCount = useMemo(
    () => pending.filter((o) => isStale(o.created_at)).length,
    [pending]
  );

  const rows = useMemo(
    () => (staleOnly ? pending.filter((o) => isStale(o.created_at)) : pending),
    [pending, staleOnly]
  );

  const reassign = async (order) => {
    const newId = choice[order.id];
    if (!newId || newId === order.requested_admin_id) {
      showToast && showToast('Pick a different faculty', 'err');
      return;
    }
    setSaving(order.id);
    try {
      await apiClient.put(endpoints.orders.superadminReassignFaculty(order.id), { new_admin_id: newId });
      showToast && showToast('Reassigned', 'ok');
      await load();
    } catch (e) { showToast && showToast(e?.response?.data?.detail || 'Failed', 'err'); }
    finally { setSaving(null); }
  };

  return (
    <div data-testid="spa-reassign-page">
      <div className="ap-eyebrow">SuperAdmin Console</div>
      <h1 className="ap-page-title">Reassign Faculty</h1>
      <p className="ap-page-lede">
        Move a pending request to a different faculty (e.g. the original approver is on leave).
        Only orders in <strong>pending</strong> status can be reassigned.
      </p>

      <div className="ap-ind-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="ap-ind" data-testid="spa-reassign-stat-pending">
          <div className="ap-ind-label"><UserCog size={11} style={{ verticalAlign: -2 }} /> Pending queue</div>
          <div className="ap-ind-value">{pending.length}</div>
          <div className="ap-ind-hint">Awaiting a faculty decision</div>
        </div>
        <div
          className="ap-ind"
          data-testid="spa-reassign-stat-stale"
          style={staleCount > 0 ? { borderColor: 'rgba(176, 74, 60, 0.4)', background: 'rgba(176, 74, 60, 0.04)' } : undefined}
        >
          <div className="ap-ind-label">
            <em className={staleCount > 0 ? 'is-exc' : ''} />
            Stale (&gt; 24h)
          </div>
          <div className="ap-ind-value" style={staleCount > 0 ? { color: '#B04A3C' } : undefined}>{staleCount}</div>
          <div className="ap-ind-hint">
            {staleCount > 0
              ? 'Request the original faculty via chat, or reassign now'
              : 'Every request is within the 24h window'}
          </div>
        </div>
        <div className="ap-ind">
          <div className="ap-ind-label">Active faculty</div>
          <div className="ap-ind-value">{faculty.length}</div>
          <div className="ap-ind-hint">Available to receive reassignments</div>
        </div>
      </div>

      {staleCount > 0 && (
        <div
          className="ap-mt-3"
          data-testid="spa-reassign-stale-banner"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            border: '1px solid rgba(176, 74, 60, 0.30)',
            background: 'rgba(176, 74, 60, 0.06)',
            borderRadius: 8,
            color: '#6B2E23',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <AlertOctagon size={18} style={{ color: '#B04A3C', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <strong>{staleCount} request{staleCount === 1 ? '' : 's'} waiting more than 24 hours.</strong>{' '}
            Students may already be blocked at the clinic — please hand these to an available faculty first.
          </div>
          <button
            type="button"
            className="ap-btn ap-btn-ghost ap-btn-sm"
            onClick={() => setStaleOnly((v) => !v)}
            data-testid="spa-reassign-stale-filter"
          >
            {staleOnly ? 'Show all' : 'Show stale only'}
          </button>
        </div>
      )}

      <div className="ap-mt-6">
        {loading ? <div className="ap-empty">Loading pending queue…</div>
          : rows.length === 0 ? (
            <div className="ap-empty" data-testid="spa-reassign-empty">
              {staleOnly
                ? 'No stale requests. Nice — everything is fresh.'
                : 'No pending orders. Nothing to reassign.'}
            </div>
          ) : (
          <div className="ap-table-wrap" data-testid="spa-reassign-table">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Student · Patient</th>
                  <th>Items</th>
                  <th>Currently with</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Waiting</th>
                  <th>Reassign to</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => {
                  const stale = isStale(o.created_at);
                  return (
                    <tr
                      key={o.id}
                      data-testid={`spa-reassign-row-${o.id}`}
                      data-stale={stale ? 'true' : undefined}
                      style={stale ? { background: 'rgba(176, 74, 60, 0.04)' } : undefined}
                    >
                      <td>
                        <div style={{ fontWeight: 500 }}>{shortRef(o.id, 'REQ')}</div>
                        <div className="ap-subtle">{fmt(o.created_at)}</div>
                      </td>
                      <td>
                        <div>{o.student_name || '—'}</div>
                        <div className="ap-subtle">
                          {o.patient_info?.name || '—'}{o.patient_info?.pid ? ` · ${o.patient_info.pid}` : ''}
                        </div>
                      </td>
                      <td>
                        {(o.items || []).slice(0, 2).map((it, i) => (
                          <div key={i} style={{ fontSize: 13 }}>{it.component_name} × {it.quantity}</div>
                        ))}
                        {(o.items || []).length > 2 && <div className="ap-subtle">+{o.items.length - 2} more</div>}
                      </td>
                      <td>{o.requested_admin_name || <span className="ap-subtle">(unassigned)</span>}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {stale ? (
                          <span
                            data-testid={`spa-reassign-stale-badge-${o.id}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '2px 8px',
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 600,
                              background: 'rgba(176, 74, 60, 0.14)',
                              color: '#8B2E1F',
                            }}
                          >
                            <Clock size={11} /> {waitingLabel(o.created_at)}
                          </span>
                        ) : (
                          <span className="ap-subtle" style={{ fontSize: 12 }}>
                            <Clock size={11} style={{ verticalAlign: -1, marginRight: 3 }} />
                            {waitingLabel(o.created_at)}
                          </span>
                        )}
                      </td>
                      <td>
                        <select
                          className="ap-input"
                          style={{ minWidth: 200 }}
                          value={choice[o.id] || ''}
                          onChange={(e) => setChoice({ ...choice, [o.id]: e.target.value })}
                          data-testid={`spa-reassign-select-${o.id}`}
                        >
                          <option value="">— pick faculty —</option>
                          {faculty.map((f) => (
                            <option key={f.id} value={f.id} disabled={f.id === o.requested_admin_id}>
                              {f.full_name}{f.id === o.requested_admin_id ? ' (current)' : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="ap-btn ap-btn-primary"
                          onClick={() => reassign(o)}
                          disabled={saving === o.id || !choice[o.id] || choice[o.id] === o.requested_admin_id}
                          data-testid={`spa-reassign-submit-${o.id}`}
                        >
                          {saving === o.id ? 'Saving…' : <>Reassign <ArrowRight size={12} style={{ marginLeft: 4 }} /></>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
